import React, { useState, useEffect, useContext, useRef } from 'react'
import { PencilLine, Trash2, X, Download, Mic, Music4, Square, Pause, Play, Check, Headphones } from 'lucide-react'
import { toast } from 'react-toastify'
import { CantiqueContextAPI } from '../Context/CantiqueContext'
import * as Api from '../Services/Api'
import { resolveCantiqueAudioUrl } from '../utils/cantiqueAudio'

const CantiqueForm = ({ isOpen, onClose, cantique = null, onSave = null }) => {
  const { availableLanguages } = useContext(CantiqueContextAPI);
  const [langue, setLangue] = useState('');
  const [titre, setTitre] = useState('')
  const [couplets, setCouplets] = useState([])
  const [refrains, setRefrains] = useState([])

  const [showCoupletInput, setShowCoupletInput] = useState(false)
  const [coupletText, setCoupletText] = useState('')
  const [editingCoupletId, setEditingCoupletId] = useState(null)

  const [showRefrainsInput, setShowRefrainsInput] = useState(false)
  const [refrainsText, setRefrainsText] = useState('')
  const [editingRefrainsId, setEditingRefrainsId] = useState(null)
  const [showAudioSection, setShowAudioSection] = useState(false)
  const [showRecordingModal, setShowRecordingModal] = useState(false)
  const [linkedAudio, setLinkedAudio] = useState(null)
  const [isLinkedAudioPlaying, setIsLinkedAudioPlaying] = useState(false)

  const ACCEPTED_AUDIO_EXTENSIONS = ['mp3', 'wav', 'webm', 'ogg', 'm4a', 'aac', 'flac', 'mpeg', 'mp4']
  const MAX_AUDIO_FILE_SIZE = 25 * 1024 * 1024

  // États audio enregistrement
  const [isRecording, setIsRecording] = useState(false)
  const [isRecordingPaused, setIsRecordingPaused] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [audioBlob, setAudioBlob] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackProgress, setPlaybackProgress] = useState(0)
  const [mediaRecorder, setMediaRecorder] = useState(null)
  const [audioElement, setAudioElement] = useState(null)
  const [audioDuration, setAudioDuration] = useState(0)
  const [playbackCurrentTime, setPlaybackCurrentTime] = useState(0)
  const [isProcessingRecording, setIsProcessingRecording] = useState(false)

  const SILENT_BAR_HEIGHT = 8
  const RECORDING_BAR_COUNT = 24
  const PREVIEW_BAR_COUNT = 48

  // Waveform en temps réel
  const [waveformBars, setWaveformBars] = useState(Array(RECORDING_BAR_COUNT).fill(SILENT_BAR_HEIGHT))
  const [previewWaveformBars, setPreviewWaveformBars] = useState(Array(PREVIEW_BAR_COUNT).fill(SILENT_BAR_HEIGHT))

  // Refs
  const audioRef = useRef(null)
  const analyserRef = useRef(null)
  const waveformAnimRef = useRef(null)
  const playbackContextRef = useRef(null)
  const playbackAnalyserRef = useRef(null)
  const playbackAnimRef = useRef(null)
  const streamRef = useRef(null)
  const audioUrlRef = useRef(null)
  const cancelRecordingRef = useRef(false)
  const mediaRecorderRef = useRef(null)
  const audioContextRef = useRef(null)
  const chunksRef = useRef([])
  const recordingTimeRef = useRef(0)
  const progressTrackRef = useRef(null)
  const isDraggingProgressRef = useRef(false)
  const playbackSourceRef = useRef(null)
  const fileInputRef = useRef(null)
  const linkedAudioRef = useRef(null)
  const linkedAudioUrlRef = useRef(null)

  const formatFileSize = (bytes) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} o`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
  }

  const getAudioExtension = (mimeType = '') => {
    if (mimeType.includes('webm')) return 'webm'
    if (mimeType.includes('wav')) return 'wav'
    if (mimeType.includes('ogg')) return 'ogg'
    if (mimeType.includes('mp4') || mimeType.includes('m4a')) return 'm4a'
    return 'mp3'
  }

  const isAcceptedAudioFile = (file) => {
    if (!file) return false
    const ext = file.name.split('.').pop()?.toLowerCase()
    return file.type.startsWith('audio/') || ACCEPTED_AUDIO_EXTENSIONS.includes(ext)
  }

  const stopLinkedAudioPreview = () => {
    if (linkedAudioRef.current) {
      linkedAudioRef.current.pause()
      linkedAudioRef.current.currentTime = 0
    }
    setIsLinkedAudioPlaying(false)
  }

  const cleanupLinkedAudioUrl = () => {
    if (linkedAudioUrlRef.current) {
      URL.revokeObjectURL(linkedAudioUrlRef.current)
      linkedAudioUrlRef.current = null
    }
  }

  useEffect(() => {
    if (isOpen && cantique) {
      setLangue(cantique.langue || availableLanguages[0]?.name || '')
      setTitre(cantique.title || '')
      if (cantique.couplets) {
        setCouplets(cantique.couplets.map((c, idx) => ({
          id: idx,
          text: c.text,
          numero: c.number ?? c.numero
        })))
      }
      if (cantique.refrain) {
        setRefrains([{ id: 0, text: cantique.refrain.text }])
      }
      if (cantique.audioUrl || cantique.hasAudio || cantique.audio) {
        setLinkedAudio({
          blob: null,
          fileName: cantique.audioFileName || 'Audio lié',
          mimeType: cantique.audioMimeType || '',
          source: cantique.audioFileName?.includes('enregistrement') ? 'recording' : 'upload',
          previewUrl: cantique.audioUrl
        })
        setShowAudioSection(true)
      } else {
        setLinkedAudio(null)
      }
    } else if (isOpen) {
      setLangue(availableLanguages[0]?.name || '')
      setTitre('')
      setCouplets([])
      setRefrains([])
      setShowCoupletInput(false)
      setShowRefrainsInput(false)
      setShowAudioSection(false)
      setLinkedAudio(null)
    }
  }, [isOpen, cantique, availableLanguages])

  useEffect(() => {
    stopLinkedAudioPreview()
    linkedAudioRef.current = null
    cleanupLinkedAudioUrl()
  }, [linkedAudio])

  useEffect(() => {
    if (isOpen && !cantique && availableLanguages.length > 0 && !langue) {
      setLangue(availableLanguages[0].name);
    }
    if (
      isOpen && cantique &&
      !availableLanguages.some(l => l.name === langue) &&
      availableLanguages.length > 0
    ) {
      setLangue(availableLanguages[0].name);
    }
  }, [availableLanguages, isOpen, cantique, langue]);

  // Timer enregistrement (en secondes réelles)
  useEffect(() => {
    let interval
    if (isRecording && !isRecordingPaused) {
      interval = setInterval(() => {
        setRecordingTime(prev => {
          const next = prev + 1
          recordingTimeRef.current = next
          return next
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isRecording, isRecordingPaused])

  const mapAnalyserToBars = (analyser, barCount) => {
    const bufferLength = analyser.frequencyBinCount
    const freqData = new Uint8Array(bufferLength)
    analyser.getByteFrequencyData(freqData)

    return Array(barCount).fill(0).map((_, i) => {
      const start = Math.floor((i / barCount) * bufferLength)
      const end = Math.max(start + 1, Math.floor(((i + 1) / barCount) * bufferLength))
      let sum = 0
      for (let j = start; j < end; j++) sum += freqData[j]
      const avg = sum / (end - start)
      if (avg < 6) return SILENT_BAR_HEIGHT
      return Math.min(100, SILENT_BAR_HEIGHT + (avg / 255) * (100 - SILENT_BAR_HEIGHT))
    })
  }

  // Waveform temps réel via AnalyserNode (réagit uniquement au son)
  const startWaveformAnimation = (analyser) => {
    const draw = () => {
      waveformAnimRef.current = requestAnimationFrame(draw)
      setWaveformBars(mapAnalyserToBars(analyser, RECORDING_BAR_COUNT))
    }
    draw()
  }

  const stopWaveformAnimation = () => {
    if (waveformAnimRef.current) {
      cancelAnimationFrame(waveformAnimRef.current)
      waveformAnimRef.current = null
    }
    setWaveformBars(Array(RECORDING_BAR_COUNT).fill(SILENT_BAR_HEIGHT))
  }

  const setupPlaybackAnalyser = async (audio) => {
    if (playbackAnalyserRef.current || !audio) return

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      await audioContext.resume()
      const source = audioContext.createMediaElementSource(audio)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 128
      analyser.smoothingTimeConstant = 0.75
      source.connect(analyser)
      analyser.connect(audioContext.destination)

      playbackContextRef.current = audioContext
      playbackSourceRef.current = source
      playbackAnalyserRef.current = analyser
    } catch (err) {
      console.warn('Impossible d\'initialiser la visualisation de lecture', err)
    }
  }

  const teardownPlaybackAnalyser = () => {
    if (playbackAnimRef.current) {
      cancelAnimationFrame(playbackAnimRef.current)
      playbackAnimRef.current = null
    }
    playbackSourceRef.current = null
    playbackAnalyserRef.current = null
    if (playbackContextRef.current) {
      playbackContextRef.current.close().catch(() => {})
      playbackContextRef.current = null
    }
    setPreviewWaveformBars(Array(PREVIEW_BAR_COUNT).fill(SILENT_BAR_HEIGHT))
  }

  const getEffectiveDuration = (audio = audioRef.current) => {
    if (!audio) return audioDuration || recordingTimeRef.current || 0
    const d = audio.duration
    if (Number.isFinite(d) && d > 0) return d
    return audioDuration || recordingTimeRef.current || 0
  }

  useEffect(() => {
    if (!audioBlob) {
      setAudioDuration(0)
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
        setAudioElement(null)
      }
      return
    }

    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }

    const url = URL.createObjectURL(audioBlob)
    audioUrlRef.current = url
    const audio = new Audio(url)
    audio.volume = 1

    const handleLoaded = () => {
      const duration = Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : recordingTimeRef.current
      setAudioDuration(duration)
      setPlaybackProgress(0)
    }

    const handleTimeUpdate = () => {
      setPlaybackCurrentTime(audio.currentTime)
      const duration = getEffectiveDuration(audio)
      if (duration > 0) {
        setPlaybackProgress((audio.currentTime / duration) * 100)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setPlaybackProgress(0)
      setPlaybackCurrentTime(0)
      audio.currentTime = 0
      stopPlaybackWaveform()
    }

    audio.addEventListener('loadedmetadata', handleLoaded)
    audio.addEventListener('durationchange', handleLoaded)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)

    audioRef.current = audio
    setAudioElement(audio)
    setIsPlaying(false)
    setPlaybackProgress(0)
    setPlaybackCurrentTime(0)
    setupPlaybackAnalyser(audio)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoaded)
      audio.removeEventListener('durationchange', handleLoaded)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.pause()
      teardownPlaybackAnalyser()
      if (audioRef.current === audio) {
        audioRef.current = null
      }
    }
  }, [audioBlob])

  useEffect(() => {
    return () => {
      if (waveformAnimRef.current) {
        cancelAnimationFrame(waveformAnimRef.current)
      }
      if (playbackAnimRef.current) {
        cancelAnimationFrame(playbackAnimRef.current)
      }
      teardownPlaybackAnalyser()
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current)
        audioUrlRef.current = null
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
    }
  }, [])

  // --- Gestion couplets ---
  const handleAddCouplet = () => {
    setShowCoupletInput(true)
    setCoupletText('')
    setEditingCoupletId(null)
  }

  const handleSaveCouplet = () => {
    if (coupletText.trim()) {
      if (editingCoupletId !== null) {
        setCouplets(couplets.map(c =>
          c.id === editingCoupletId ? { ...c, text: coupletText } : c
        ))
        setEditingCoupletId(null)
      } else {
        setCouplets([...couplets, {
          id: Date.now(),
          text: coupletText,
          numero: couplets.length + 1
        }])
      }
      setCoupletText('')
      setShowCoupletInput(false)
    }
  }

  const handleCancelCouplet = () => {
    setCoupletText('')
    setShowCoupletInput(false)
    setEditingCoupletId(null)
  }

  const handleEditCouplet = (id) => {
    const couplet = couplets.find(c => c.id === id)
    setCoupletText(couplet.text)
    setEditingCoupletId(id)
    setShowCoupletInput(true)
  }

  const handleDeleteCouplet = (id) => {
    setCouplets(couplets.filter(c => c.id !== id))
  }

  // --- Gestion refrains ---
  const handleAddRefrain = () => {
    setShowRefrainsInput(true)
    setRefrainsText('')
    setEditingRefrainsId(null)
  }

  const handleSaveRefrain = () => {
    if (refrainsText.trim()) {
      if (editingRefrainsId !== null) {
        setRefrains(refrains.map(r =>
          r.id === editingRefrainsId ? { ...r, text: refrainsText } : r
        ))
        setEditingRefrainsId(null)
      } else {
        setRefrains([...refrains, { id: Date.now(), text: refrainsText }])
      }
      setRefrainsText('')
      setShowRefrainsInput(false)
    }
  }

  const handleCancelRefrain = () => {
    setRefrainsText('')
    setShowRefrainsInput(false)
    setEditingRefrainsId(null)
  }

  const handleEditRefrain = (id) => {
    const refrain = refrains.find(r => r.id === id)
    setRefrainsText(refrain.text)
    setEditingRefrainsId(id)
    setShowRefrainsInput(true)
  }

  const handleDeleteRefrain = (id) => {
    setRefrains(refrains.filter(r => r.id !== id))
  }

  const handleAddAudio = () => setShowAudioSection(true)
  const handleCancelAudio = () => {
    stopLinkedAudioPreview()
    cleanupLinkedAudioUrl()
    setLinkedAudio(null)
    setShowAudioSection(false)
  }

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleAudioFileChange = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (!isAcceptedAudioFile(file)) {
      alert('Format non supporté. Utilisez un fichier mp3, wav, webm, ogg, m4a, aac ou flac.')
      return
    }

    if (file.size > MAX_AUDIO_FILE_SIZE) {
      alert('Fichier trop volumineux. Taille maximale : 25 Mo.')
      return
    }

    stopLinkedAudioPreview()
    cleanupLinkedAudioUrl()
    setLinkedAudio({
      blob: file,
      fileName: file.name,
      mimeType: file.type || 'audio/mpeg',
      source: 'upload'
    })
  }

  const handleAssociateRecording = () => {
    if (!audioBlob) return

    stopLinkedAudioPreview()
    cleanupLinkedAudioUrl()
    setLinkedAudio({
      blob: audioBlob,
      fileName: `enregistrement-${formatTime(recordingTime).replace(':', '-')}.${getAudioExtension(audioBlob.type)}`,
      mimeType: audioBlob.type || 'audio/webm',
      source: 'recording'
    })
    setShowRecordingModal(false)
  }

  const handleRemoveLinkedAudio = () => {
    stopLinkedAudioPreview()
    cleanupLinkedAudioUrl()
    setLinkedAudio(null)
  }

  const handleToggleLinkedAudioPreview = async () => {
    if (!linkedAudio) return

    if (isLinkedAudioPlaying) {
      stopLinkedAudioPreview()
      return
    }

    try {
      if (!linkedAudioRef.current) {
        let src = resolveCantiqueAudioUrl(linkedAudio.previewUrl)
        if (!src && linkedAudio.blob) {
          cleanupLinkedAudioUrl()
          src = URL.createObjectURL(linkedAudio.blob)
          linkedAudioUrlRef.current = src
        }
        if (!src) return

        const audio = new Audio(src)
        audio.volume = 1
        audio.addEventListener('ended', () => setIsLinkedAudioPlaying(false))
        linkedAudioRef.current = audio
      }

      await linkedAudioRef.current.play()
      setIsLinkedAudioPlaying(true)
    } catch (error) {
      console.error('Erreur lecture audio lié:', error)
      setIsLinkedAudioPlaying(false)
    }
  }

  const blobToUploadFile = (blob, fileName, mimeType) => {
    if (blob instanceof File) return blob
    return new File([blob], fileName || 'audio.webm', {
      type: mimeType || blob.type || 'audio/webm',
    })
  }

  const isServerAudioUrl = (url) => {
    if (!url || typeof url !== 'string') return false
    return url.startsWith('/api/uploads/') || url.startsWith('http://') || url.startsWith('https://')
  }

  const buildAudioPayload = async () => {
    if (!linkedAudio) {
      return {
        hasAudio: false,
        audio: false,
        audioUrl: null,
        audioFileName: null,
        audioMimeType: null,
      }
    }

    if (linkedAudio.previewUrl && !linkedAudio.blob && isServerAudioUrl(linkedAudio.previewUrl)) {
      return {
        hasAudio: true,
        audio: true,
        audioUrl: linkedAudio.previewUrl,
        audioFileName: linkedAudio.fileName,
        audioMimeType: linkedAudio.mimeType || null,
      }
    }

    if (!linkedAudio.blob) return null

    try {
      const file = blobToUploadFile(
        linkedAudio.blob,
        linkedAudio.fileName,
        linkedAudio.mimeType || linkedAudio.blob.type
      )
      const response = await Api.uploadCantiqueAudio(file, titre || file.name)
      return response.data
    } catch (error) {
      console.error('Erreur upload audio:', error)
      toast.error(`Échec de l'upload audio : ${error.response?.data?.message || error.message}`)
      return null
    }
  }
  const handleOpenRecordingModal = () => setShowRecordingModal(true)
  const handleCloseRecordingModal = () => {
    if (isRecording || isRecordingPaused) handleCancelRecording()
    setShowRecordingModal(false)
  }

  const hasActiveRecordingSession = isRecording || isRecordingPaused

  const getSupportedMimeType = () => {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
    return types.find(type => MediaRecorder.isTypeSupported(type)) || ''
  }

  const finalizeRecording = () => {
    const recorder = mediaRecorderRef.current
    if (!recorder || recorder.state === 'inactive') return

    setIsProcessingRecording(true)

    if (recorder.state === 'recording') {
      try {
        recorder.requestData()
      } catch (_) {}
    }
    recorder.stop()
    stopWaveformAnimation()
  }

  // --- Enregistrement audio ---
  const startPlaybackWaveform = () => {
    const analyser = playbackAnalyserRef.current
    if (!analyser || playbackAnimRef.current) return

    const draw = () => {
      playbackAnimRef.current = requestAnimationFrame(draw)
      setPreviewWaveformBars(mapAnalyserToBars(analyser, PREVIEW_BAR_COUNT))
    }
    draw()
  }

  const stopPlaybackWaveform = () => {
    if (playbackAnimRef.current) {
      cancelAnimationFrame(playbackAnimRef.current)
      playbackAnimRef.current = null
    }
    setPreviewWaveformBars(Array(PREVIEW_BAR_COUNT).fill(SILENT_BAR_HEIGHT))
  }

  const handleStartRecording = async () => {
    const activeRecorder = mediaRecorderRef.current
    if (
      hasActiveRecordingSession ||
      (activeRecorder && (activeRecorder.state === 'recording' || activeRecorder.state === 'paused'))
    ) return

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      cancelRecordingRef.current = false
      chunksRef.current = []

      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      audioContextRef.current = audioContext
      await audioContext.resume()

      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.8
      source.connect(analyser)
      analyserRef.current = analyser
      startWaveformAnimation(analyser)

      const mimeType = getSupportedMimeType()
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      recorder.onstop = () => {
        const wasCancelled = cancelRecordingRef.current
        cancelRecordingRef.current = false

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop())
          streamRef.current = null
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {})
          audioContextRef.current = null
        }
        stopWaveformAnimation()
        mediaRecorderRef.current = null
        setMediaRecorder(null)

        if (wasCancelled) {
          chunksRef.current = []
          setIsProcessingRecording(false)
          return
        }

        const blobType = mimeType || chunksRef.current[0]?.type || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: blobType })
        chunksRef.current = []

        setIsRecording(false)
        setIsRecordingPaused(false)

        if (blob.size > 0) {
          window.setTimeout(() => {
            setAudioBlob(blob)
            setPlaybackProgress(0)
            setIsPlaying(false)
            stopPlaybackWaveform()
            setIsProcessingRecording(false)
          }, 450)
        } else {
          setIsProcessingRecording(false)
        }
      }

      mediaRecorderRef.current = recorder
      setMediaRecorder(recorder)
      setIsRecording(true)
      setIsRecordingPaused(false)
      setRecordingTime(0)
      recordingTimeRef.current = 0
      setAudioBlob(null)
      setAudioDuration(0)
      stopPlaybackWaveform()

      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current)
        audioUrlRef.current = null
      }
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
        setAudioElement(null)
      }

      recorder.start(250)
    } catch (error) {
      console.error('Erreur accès microphone:', error)
      setIsRecording(false)
      alert('Impossible d\'accéder au microphone. Vérifiez les permissions de votre navigateur.')
    }
  }

  const handleToggleRecordingPause = () => {
    const recorder = mediaRecorderRef.current
    if (!recorder) return

    try {
      if (recorder.state === 'recording') {
        recorder.pause()
        setIsRecording(false)
        setIsRecordingPaused(true)
        stopWaveformAnimation()
        return
      }

      if (recorder.state === 'paused') {
        recorder.resume()
        setIsRecording(true)
        setIsRecordingPaused(false)
        if (analyserRef.current) {
          startWaveformAnimation(analyserRef.current)
        }
      }
    } catch (error) {
      console.error('Pause/Reprise non supportée:', error)
      alert('La pause n\'est pas supportée par votre navigateur. Utilisez Terminer pour finaliser.')
    }
  }

  const handleFinishRecording = () => {
    if (!hasActiveRecordingSession) return
    finalizeRecording()
  }

  const handleCancelRecording = () => {
    const recorder = mediaRecorderRef.current

    if (recorder) {
      cancelRecordingRef.current = true
      if (recorder.state !== 'inactive') {
        try {
          recorder.requestData()
        } catch (_) {}
        recorder.stop()
      }
      setIsRecording(false)
      setIsRecordingPaused(false)
      setIsProcessingRecording(false)
      setRecordingTime(0)
      recordingTimeRef.current = 0
      setAudioBlob(null)
      chunksRef.current = []
      stopWaveformAnimation()
      mediaRecorderRef.current = null
      setMediaRecorder(null)
      return
    }

    if (audioBlob) {
      setAudioBlob(null)
      setPlaybackProgress(0)
      setPlaybackCurrentTime(0)
      setAudioDuration(0)
      setIsPlaying(false)
      teardownPlaybackAnalyser()
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
        setAudioElement(null)
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current)
        audioUrlRef.current = null
      }
    }
  }

  // --- Lecture audio ---
  const handlePlayAudio = async () => {
    if (!audioBlob || !audioRef.current) return

    try {
      audioRef.current.volume = 1
      if (playbackContextRef.current?.state === 'suspended') {
        await playbackContextRef.current.resume()
      }
      await audioRef.current.play()
      setIsPlaying(true)
      startPlaybackWaveform()
    } catch (error) {
      console.error('Erreur lecture audio:', error)
    }
  }

  const handleStopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsPlaying(false)
      setPlaybackProgress(0)
      setPlaybackCurrentTime(0)
      stopPlaybackWaveform()
    }
  }

  const handlePauseAudio = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
      stopPlaybackWaveform()
    }
  }

  const seekToProgressPosition = (clientX) => {
    const track = progressTrackRef.current
    const audio = audioRef.current
    if (!track || !audio || !audioBlob) return

    const duration = getEffectiveDuration(audio)
    if (!duration) return

    const rect = track.getBoundingClientRect()
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const newTime = percentage * duration

    audio.currentTime = newTime
    setPlaybackProgress(percentage * 100)
    setPlaybackCurrentTime(newTime)
  }

  useEffect(() => {
    const handleMove = (e) => {
      if (!isDraggingProgressRef.current) return
      seekToProgressPosition(e.clientX)
    }
    const handleUp = () => {
      isDraggingProgressRef.current = false
    }
    const handleTouchMove = (e) => {
      if (!isDraggingProgressRef.current || !e.touches[0]) return
      seekToProgressPosition(e.touches[0].clientX)
    }

    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
    document.addEventListener('touchmove', handleTouchMove, { passive: true })
    document.addEventListener('touchend', handleUp)

    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleUp)
    }
  }, [audioBlob, audioDuration])

  const handleProgressMouseDown = (e) => {
    if (!audioBlob) return
    e.preventDefault()
    isDraggingProgressRef.current = true
    seekToProgressPosition(e.clientX)
  }

  const handleProgressTouchStart = (e) => {
    if (!audioBlob || !e.touches[0]) return
    isDraggingProgressRef.current = true
    seekToProgressPosition(e.touches[0].clientX)
  }

  // Formatage du temps
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      cancelRecordingRef.current = true
      mediaRecorderRef.current.stop()
    }
    mediaRecorderRef.current = null
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    stopWaveformAnimation()
    teardownPlaybackAnalyser()
    setLangue(availableLanguages[0]?.name || '')
    setTitre('')
    setCoupletText('')
    setShowCoupletInput(false)
    setEditingCoupletId(null)
    setRefrainsText('')
    setShowRefrainsInput(false)
    setEditingRefrainsId(null)
    setShowAudioSection(false)
    setShowRecordingModal(false)
    setAudioElement(null)
    setAudioBlob(null)
    setPlaybackProgress(0)
    setPlaybackCurrentTime(0)
    setIsPlaying(false)
    setIsRecording(false)
    setIsRecordingPaused(false)
    setIsProcessingRecording(false)
    setRecordingTime(0)
    setAudioDuration(0)
    stopLinkedAudioPreview()
    cleanupLinkedAudioUrl()
    linkedAudioRef.current = null
    setLinkedAudio(null)
    setIsLinkedAudioPlaying(false)
    onClose()
  }

  if (!isOpen) return null

  const recordingStatusLabel = isProcessingRecording
    ? 'Finalisation en cours...'
    : isRecordingPaused
    ? 'En pause'
    : isRecording
      ? 'Enregistrement en cours...'
      : audioBlob
        ? 'Enregistrement terminé'
        : 'Prêt à enregistrer'

  const recordingSubLabel = isRecordingPaused
    ? 'Appuyez sur Reprendre pour continuer'
    : isRecording
      ? 'Enregistrement en cours...'
      : 'Cliquez sur Enregistrer pour démarrer'

  return (
    <div className="cantForm" onClick={handleBackdropClick}>
      <div className="cantForm__container">
        <button className="cantForm__close" onClick={handleClose}>
          <X size={24} />
        </button>

        <h2 className="cantForm__title">Nouveau Cantique</h2>
        <div className="cantForm__title__sub">Remplissez les informations du cantique</div>

        <form className="cantForm__form" onSubmit={(e) => e.preventDefault()}>
          <div className="cantForm__group">
            <div className="cantForm__group__card">
              <label className='cantForm__label__top' htmlFor="langue">Langue du cantique</label>
              <select
                className='cantForm__input__top'
                id="langue"
                name="langue"
                value={langue}
                onChange={(e) => setLangue(e.target.value)}
              >
                {availableLanguages.length === 0 ? (
                  <option value="" disabled>Chargement...</option>
                ) : (
                  availableLanguages.map(lang => (
                    <option key={lang.id} value={lang.name}>{lang.name}</option>
                  ))
                )}
              </select>
            </div>
            <div className="cantForm__group__card">
              <label className='cantForm__label__top' htmlFor="titre">Titre du cantique</label>
              <input
                className='cantForm__input__top'
                type="text"
                id="titre"
                name="titre"
                placeholder="Entrez le titre du cantique"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
              />
            </div>
          </div>

          {/* Audio section */}
          <div className="cantForm__group__audios__bloc">
            <div className="cantForm__group__audios__header">
              <div>
                <h2 className="cantForm__group__audios__top__title">Audio et enregistrement</h2>
                <span className="cantForm__group__audios__badge">★ Nouvelle fonctinnalité</span>
              </div>
              <button
                type="button"
                className="cantForm__group__top__audios__btn"
                onClick={handleAddAudio}
              >
                <span>+</span>
                <span>Ajouter un audio</span>
              </button>
            </div>

            {showAudioSection && (
              <div className="cantForm__group__audios">
                <div className="cantForm__group__audios__btns">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="cantForm__group__audios__btns__input"
                    accept="audio/*,.mp3,.wav,.webm,.ogg,.m4a,.aac,.flac"
                    onChange={handleAudioFileChange}
                  />
                  <button
                    type="button"
                    className="cantForm__group__audios__btn"
                    onClick={handleUploadButtonClick}
                  >
                    <span><Download size={14} /></span>
                    <span>Téléverser un fichier</span>
                  </button>
                  <button
                    type="button"
                    className="cantForm__group__audios__btn"
                    onClick={handleOpenRecordingModal}
                  >
                    <span><Mic size={14} /></span>
                    <span>Enregistrer un nouveau audio</span>
                  </button>
                </div>
                <div className="cantForm__group__audios__response">
                  <div className={`cantForm__group__audios__response__item ${linkedAudio ? 'cantForm__group__audios__response__item--linked' : ''}`}>
                    <div className="cantForm__group__audios__response__item__images">
                      <span>{linkedAudio ? <Headphones size={20} /> : <Music4 size={20} />}</span>
                    </div>
                    <div className="cantForm__group__audios__response__item__text">
                      {linkedAudio ? (
                        <>
                          <h3 className="cantForm__group__audios__response__item__text__title">
                            {linkedAudio.fileName}
                          </h3>
                          <span className="cantForm__group__audios__response__item__text__sub">
                            {linkedAudio.source === 'recording'
                              ? 'Enregistrement vocal lié à ce cantique'
                              : 'Fichier audio importé et lié à ce cantique'}
                            {linkedAudio.blob?.size ? ` · ${formatFileSize(linkedAudio.blob.size)}` : ''}
                          </span>
                        </>
                      ) : (
                        <>
                          <h3 className="cantForm__group__audios__response__item__text__title">Aucun audio lié</h3>
                          <span className="cantForm__group__audios__response__item__text__sub">
                            Téléversez un fichier (mp3, wav, webm…) ou enregistrez un nouvel audio
                          </span>
                        </>
                      )}
                    </div>
                    {linkedAudio ? (
                      <div className="cantForm__group__audios__response__item__actions">
                        <button
                          type="button"
                          className="cantForm__group__audios__response__item__actions__play"
                          onClick={handleToggleLinkedAudioPreview}
                          title={isLinkedAudioPlaying ? 'Pause' : 'Écouter'}
                        >
                          {isLinkedAudioPlaying ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <button
                          type="button"
                          className="cantForm__group__audios__response__item__actions__remove"
                          onClick={handleRemoveLinkedAudio}
                          title="Retirer l'audio"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="cantForm__group__audios__response__item__btn__fixed">
                        <span>Mp3, wav, m4a</span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="cantForm__group__audios__response__item__cancel"
                    onClick={handleCancelAudio}
                  >
                    {linkedAudio ? 'Fermer' : 'Annuler'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Modal enregistrement audio */}
          {showRecordingModal && (
          <div className="cantForm__audioModal" onClick={(e) => { if (e.target === e.currentTarget) handleCloseRecordingModal() }}>
            <div className="cantForm__audioModal__container">
              <button
                type="button"
                className="cantForm__audioModal__close"
                onClick={handleCloseRecordingModal}
                title="Fermer"
              >
                <X size={20} />
              </button>
              <h2 className="cantForm__audioModal__title">Enregistrement audio</h2>
              <div className="cantForm__audioModal__sub">
                Capturez votre voix et associez-la à ce cantique. Utilisez Pause pour interrompre, Reprendre pour continuer, puis Terminer pour valider.
              </div>

              <div className="cantForm__audioModal__sections">

                {/* Section enregistrement */}
                <div className="cantForm__audioModal__sections__record">
                  <div className="cantForm__audioModal__sections__record__card cantForm__audioModal__sections__record__card__top">
                    <div className="cantForm__audioModal__sections__record__card__top__item">
                      <div className={`cantForm__audioModal__sections__record__card__top__item__round ${isRecording && !isRecordingPaused ? 'recording-pulse' : ''}`}>
                        <div className={`cantForm__audioModal__sections__record__card__top__item__round__active ${isRecording && !isRecordingPaused ? 'recording-active' : ''}`}>
                          <span><Mic size={32} /></span>
                        </div>
                      </div>
                    </div>
                    <div className="cantForm__audioModal__sections__record__card__top__item">
                      <h3 className="cantForm__audioModal__sections__record__card__top__item__title">
                        <span className="cantForm__audioModal__sections__record__card__top__item__title__round__red"></span>
                        <span>{recordingStatusLabel}</span>
                      </h3>
                      <div className="cantForm__audioModal__sections__record__card__top__item__chronominutes">
                        <span>{formatTime(recordingTime)}</span>
                        <span>{recordingSubLabel}</span>
                      </div>
                      <div className="cantForm__audioModal__sections__record__card__top__item__audio__waveform">
                        <div className={`cantForm__audioModal__sections__preview__item__waveforming__waveform ${isRecording && !isRecordingPaused ? 'waveform-bars--live' : ''}`}>
                          {waveformBars.map((height, i) => (
                            <div
                              key={i}
                              className="bar"
                              style={{ height: `${height}%` }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="cantForm__audioModal__sections__record__card cantForm__audioModal__sections__record__card__bottom">
                    <div className="cantForm__audioModal__sections__record__card__bottom__item">
                      <div
                        className={`cantForm__audioModal__sections__record__bottom__item__icone orange ${hasActiveRecordingSession ? '' : 'disabled'}`}
                        onClick={hasActiveRecordingSession ? handleToggleRecordingPause : undefined}
                        title={isRecordingPaused ? 'Reprendre l\'enregistrement' : 'Mettre en pause'}
                        role="button"
                        aria-disabled={!hasActiveRecordingSession}
                      >
                        {isRecordingPaused ? <Play size={18} /> : <Pause size={18} />}
                      </div>
                      <span>{isRecordingPaused ? 'Reprendre' : 'Pause'}</span>
                    </div>
                    <div className="cantForm__audioModal__sections__record__card__bottom__item">
                      <div
                        className={`cantForm__audioModal__sections__record__bottom__item__icone red ${isRecording ? 'active-btn' : ''} ${hasActiveRecordingSession ? 'disabled' : ''}`}
                        onClick={!hasActiveRecordingSession ? handleStartRecording : undefined}
                        title={hasActiveRecordingSession ? 'Enregistrement en cours' : 'Démarrer l\'enregistrement'}
                        role="button"
                        aria-disabled={hasActiveRecordingSession}
                      >
                        <Mic size={24} />
                      </div>
                      <span>{hasActiveRecordingSession ? 'En cours' : 'Enregistrer'}</span>
                    </div>
                    <div className="cantForm__audioModal__sections__record__card__bottom__item">
                      <div
                        className={`cantForm__audioModal__sections__record__bottom__item__icone blue ${!hasActiveRecordingSession && audioBlob ? 'done-btn' : ''} ${!hasActiveRecordingSession && !audioBlob ? 'disabled' : ''}`}
                        onClick={hasActiveRecordingSession ? handleFinishRecording : undefined}
                        title={hasActiveRecordingSession ? 'Terminer et sauvegarder' : audioBlob ? 'Enregistrement terminé' : 'Terminer l\'enregistrement'}
                        role="button"
                        aria-disabled={!hasActiveRecordingSession}
                      >
                        {hasActiveRecordingSession ? <Square size={18} /> : <Check size={18} />}
                      </div>
                      <span>{hasActiveRecordingSession ? 'Terminer' : audioBlob ? 'Terminé' : 'Terminer'}</span>
                    </div>
                    <div className="cantForm__audioModal__sections__record__card__bottom__item">
                      <div
                        className={`cantForm__audioModal__sections__record__bottom__item__icone white ${!hasActiveRecordingSession && !audioBlob ? 'disabled' : ''}`}
                        onClick={(hasActiveRecordingSession || audioBlob) ? handleCancelRecording : undefined}
                        title={hasActiveRecordingSession ? 'Annuler l\'enregistrement' : audioBlob ? 'Supprimer l\'enregistrement' : 'Annuler'}
                        role="button"
                        aria-disabled={!hasActiveRecordingSession && !audioBlob}
                      >
                        {hasActiveRecordingSession ? <X size={18} /> : <Trash2 size={18} />}
                      </div>
                      <span>{hasActiveRecordingSession ? 'Annuler' : audioBlob ? 'Supprimer' : 'Annuler'}</span>
                    </div>
                  </div>
                </div>

                {/* Section aperçu */}
                <div className="cantForm__audioModal__sections__preview">
                  <div className="cantForm__audioModal__sections__preview__top">
                    <h3 className="cantForm__audioModal__sections__preview__top__title">Aperçu de votre enregistrement</h3>
                    <div className="cantForm__audioModal__sections__preview__top__minutes">
                      {formatTime(playbackCurrentTime)} / {formatTime(audioDuration)}
                    </div>
                  </div>

                  <div className="cantForm__audioModal__sections__preview__list">
                    {isProcessingRecording ? (
                      <div className="cantForm__audioModal__sections__preview__loading">
                        <div className="loading-spinner cantForm__audioModal__sections__preview__loading__spinner" />
                        <p className="cantForm__audioModal__sections__preview__loading__text">
                          Préparation de l&apos;aperçu...
                        </p>
                      </div>
                    ) : (
                      <>
                    {/* Bouton play/pause */}
                    <div className="cantForm__audioModal__sections__preview__item__playing">
                      {isPlaying ? (
                        <div
                          className="cantForm__audioModal__sections__preview__item__play"
                          onClick={handlePauseAudio}
                          style={{ cursor: 'pointer' }}
                          title="Pause"
                        >
                          <span><Pause size={14} /></span>
                        </div>
                      ) : (
                        <div
                          className="cantForm__audioModal__sections__preview__item__play"
                          onClick={handlePlayAudio}
                          style={{ cursor: audioBlob ? 'pointer' : 'not-allowed', opacity: audioBlob ? 1 : 0.4 }}
                          title={audioBlob ? 'Lire' : 'Aucun enregistrement'}
                        >
                          <span><Play size={14} /></span>
                        </div>
                      )}
                    </div>

                    {/* Waveform + barre de progression */}
                    <div className="cantForm__audioModal__sections__preview__item__waveforming">
                      <div className={`cantForm__audioModal__sections__preview__item__waveforming__waveform ${isPlaying ? 'waveform-bars--live' : ''}`}>
                        {previewWaveformBars.map((height, i) => (
                          <div
                            key={i}
                            className="bar"
                            style={{ height: `${height}%` }}
                          />
                        ))}
                      </div>

                      {/* Barre de progression redesignée */}
                      <div
                        ref={progressTrackRef}
                        className="cantForm__audioModal__sections__preview__item__waveforming__progress__track"
                        onMouseDown={handleProgressMouseDown}
                        onTouchStart={handleProgressTouchStart}
                        style={{ cursor: audioBlob ? 'pointer' : 'default' }}
                        title="Cliquez ou glissez pour changer la position"
                      >
                        <div
                          className="cantForm__audioModal__sections__preview__item__waveforming__progress__fill"
                          style={{ width: `${playbackProgress}%` }}
                        />
                        {/* Curseur rond sur la barre */}
                        <div
                          className="cantForm__audioModal__sections__preview__item__waveforming__progress__thumb"
                          style={{ left: `${playbackProgress}%` }}
                        />
                      </div>
                    </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Boutons submit */}
                <div className="cantForm__audioModal__sections__submits">
                  <button
                    type="button"
                    className="cantForm__audioModal__sections__submits__submit"
                    disabled={!audioBlob}
                    onClick={handleAssociateRecording}
                    style={{ opacity: audioBlob ? 1 : 0.5, cursor: audioBlob ? 'pointer' : 'not-allowed' }}
                  >
                    Enregistrer &amp; associer
                  </button>
                </div>

              </div>
            </div>
          </div>
          )}

          {/* Section Couplets */}
          <div className="cantForm__preview">
            <h3 className="cantForm__preview__title">Paroles du cantique</h3>
            <button type="button" className="cantForm__preview__btn" onClick={handleAddCouplet}>
              <span>+</span>
              <span>Ajouter une couplet</span>
            </button>

            {showCoupletInput && (
              <div className="cantForm__preview__textareas">
                <textarea
                  placeholder='Jésus-Christe est ma sagesse'
                  className="cantForm__preview__textarea"
                  value={coupletText}
                  onChange={(e) => setCoupletText(e.target.value)}
                  autoFocus
                />
                <div className="cantForm__preview__textareas__btns">
                  <button type="button" className="cantForm__preview__textareas__btn" onClick={handleSaveCouplet}>
                    {editingCoupletId !== null ? 'Modifier ce couplet' : 'Enregistrer ce couplet'}
                  </button>
                  <button type="button" className="cantForm__preview__textareas__btn" onClick={handleCancelCouplet}>
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {couplets.length > 0 && (
              <div className="cantForm__preview__couplets">
                {couplets.map((couplet) => (
                  <div key={couplet.id} className="cantForm__preview__couplets__couplet">
                    <div className="cantForm__preview__couplets__numero">Couplet {couplet.numero}</div>
                    <div className="cantForm__preview__couplets__text">{couplet.text}</div>
                    <div className="cantForm__preview__couplets__btns">
                      <button type="button" className="cantForm__preview__couplets__btn" onClick={() => handleEditCouplet(couplet.id)}>
                        <span><PencilLine size={14} /></span>
                        <span>Modifier ce couplet</span>
                      </button>
                      <button type="button" className="cantForm__preview__couplets__btn" onClick={() => handleDeleteCouplet(couplet.id)}>
                        <span><Trash2 size={14} /></span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section Refrains */}
          <div className="cantForm__refrains">
            <h4 className="cantForm__refrains__title">Ajouter un refrain à ce cantique</h4>
            <button type="button" className="cantForm__preview__btn" onClick={handleAddRefrain}>
              <span>+</span>
              <span>Ajouter un refrain</span>
            </button>

            {showRefrainsInput && (
              <div className="cantForm__refrains__textareas">
                <textarea
                  placeholder='Ecrivez le refrains ici'
                  className="cantForm__refrains__textarea"
                  value={refrainsText}
                  onChange={(e) => setRefrainsText(e.target.value)}
                  autoFocus
                />
                <div className="cantForm__refrains__textareas__btns">
                  <button type="button" className="cantForm__refrains__textareas__btn" onClick={handleSaveRefrain}>
                    {editingRefrainsId !== null ? 'Modifier ce refrain' : 'Enregistrer'}
                  </button>
                  <button type="button" className="cantForm__refrains__textareas__btn" onClick={handleCancelRefrain}>
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {refrains.length > 0 && (
              <div className="cantForm__refrains__preview">
                {refrains.map((refrain) => (
                  <div key={refrain.id} className="cantForm__refrains__preview__item">
                    <div className="cantForm__refrains__preview__text">{refrain.text}</div>
                    <div className="cantForm__refrains__preview__btns">
                      <button type="button" className="cantForm__refrains__preview__btn" onClick={() => handleEditRefrain(refrain.id)}>
                        <span><PencilLine size={14} /></span>
                        <span>Modifier ce refrain</span>
                      </button>
                      <button type="button" className="cantForm__refrains__preview__btn" onClick={() => handleDeleteRefrain(refrain.id)}>
                        <span><Trash2 size={14} /></span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            className='cantForm__submit'
            type="button"
            onClick={async () => {
              if (!titre.trim()) return

              const audioPayload = await buildAudioPayload()
              if (audioPayload === null) return

              const formData = {
                id: cantique?.id,
                title: titre,
                langue: langue,
                couplets: couplets,
                refrain: refrains.length > 0 ? { text: refrains[0].text } : null,
                ...audioPayload,
              }

              if (onSave) {
                const saved = await onSave(formData)
                if (saved === false) return
              }
              handleClose()
            }}
          >
            {cantique ? 'Modifier ce cantique' : 'Créer ce cantique'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default CantiqueForm